import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, MagicWand, Sparkle, CircleNotch } from '@phosphor-icons/react';
import gradesService from '../services/gradesService';
import './ReportCardRemarksModal.css';

const BLANK_FORM = {
  class_teacher_remarks: '',
  head_teacher_remarks: '',
  attitude: '',
  conduct: '',
  interest: '',
  vacation_date: '',
  reopening_date: '',
  promoted_to: '',
  promotion_decision: ''
};

/**
 * Lets a teacher/admin fill in the GES terminal-report narrative fields
 * (remarks, attitude/conduct/interest, vacation/reopening dates, promotion)
 * that generate_report_card persists but the auto-generate-on-preview path
 * never sets. Saving re-generates the report card, which resets it to DRAFT
 * so a head teacher has to re-approve the edited version.
 */
function ReportCardRemarksModal({ isOpen, onClose, studentId, academicTermId, initialData, onSaved }) {
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({
        class_teacher_remarks: initialData?.class_teacher_remarks || '',
        head_teacher_remarks: initialData?.head_teacher_remarks || '',
        attitude: initialData?.attitude || '',
        conduct: initialData?.conduct || '',
        interest: initialData?.interest || '',
        vacation_date: initialData?.vacation_date || '',
        reopening_date: initialData?.reopening_date || '',
        promoted_to: initialData?.promoted_to || '',
        promotion_decision: initialData?.promotion_decision || ''
      });
    }
  }, [isOpen, initialData]);

  if (!isOpen) return null;

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const applySuggestion = (suggestion) => {
    setForm(prev => ({
      ...prev,
      class_teacher_remarks: suggestion.class_teacher_remarks || prev.class_teacher_remarks,
      head_teacher_remarks: suggestion.head_teacher_remarks || prev.head_teacher_remarks
    }));
  };

  const handleFreeSuggestion = async () => {
    setSuggesting(true);
    try {
      const suggestion = await gradesService.suggestRemarks(studentId, academicTermId);
      applySuggestion(suggestion);
      toast.success('Suggested remarks applied — edit as needed');
    } catch (error) {
      toast.error(error.message || 'Failed to get suggested remarks');
    } finally {
      setSuggesting(false);
    }
  };

  const handleAiSuggestion = async () => {
    setAiSuggesting(true);
    try {
      const suggestion = await gradesService.aiSuggestRemarks(studentId, academicTermId);
      applySuggestion(suggestion);
      toast.success('AI-suggested remarks applied — edit as needed');
    } catch (error) {
      toast.error(error.message || 'AI remarks are not available for this school');
    } finally {
      setAiSuggesting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await gradesService.generateReportCard({
        student_id: studentId,
        academic_term_id: academicTermId,
        ...form,
        promotion_decision: form.promotion_decision || null
      });
      toast.success('Report card details saved');
      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save report card details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} data-testid="report-card-remarks-modal">
        <div className="modal-header">
          <h2>Report Card Details</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSave} className="modal-form">
          <div className="remarks-suggest-row">
            <button type="button" className="btn btn-sm btn-secondary" onClick={handleFreeSuggestion} disabled={suggesting || aiSuggesting}>
              {suggesting ? <CircleNotch size={14} className="spinner" /> : <MagicWand size={14} />}
              {suggesting ? 'Suggesting...' : 'Free Suggestion'}
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={handleAiSuggestion} disabled={suggesting || aiSuggesting}>
              {aiSuggesting ? <CircleNotch size={14} className="spinner" /> : <Sparkle size={14} />}
              {aiSuggesting ? 'Asking AI...' : 'Try AI Suggestion'}
            </button>
            <span className="remarks-suggest-hint">Fills in remarks below — review and edit before saving</span>
          </div>

          <div className="form-group">
            <label>Class Teacher's Remarks</label>
            <textarea
              value={form.class_teacher_remarks}
              onChange={(e) => updateField('class_teacher_remarks', e.target.value)}
              rows={3}
              disabled={saving}
              data-testid="class-teacher-remarks-input"
            />
          </div>
          <div className="form-group">
            <label>Headteacher's Remarks</label>
            <textarea
              value={form.head_teacher_remarks}
              onChange={(e) => updateField('head_teacher_remarks', e.target.value)}
              rows={3}
              disabled={saving}
              data-testid="head-teacher-remarks-input"
            />
          </div>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label>Attitude</label>
              <input
                type="text"
                value={form.attitude}
                onChange={(e) => updateField('attitude', e.target.value)}
                placeholder="e.g., Excellent"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Conduct</label>
              <input
                type="text"
                value={form.conduct}
                onChange={(e) => updateField('conduct', e.target.value)}
                placeholder="e.g., Good"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Interest</label>
              <input
                type="text"
                value={form.interest}
                onChange={(e) => updateField('interest', e.target.value)}
                placeholder="e.g., Sports, Reading"
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Vacation Date</label>
              <input
                type="text"
                value={form.vacation_date}
                onChange={(e) => updateField('vacation_date', e.target.value)}
                placeholder="e.g., 12th December, 2026"
                disabled={saving}
              />
            </div>
            <div className="form-group">
              <label>Reopening Date</label>
              <input
                type="text"
                value={form.reopening_date}
                onChange={(e) => updateField('reopening_date', e.target.value)}
                placeholder="e.g., 6th January, 2027"
                disabled={saving}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Promotion Decision <span className="field-hint">(Term 3 only — drives year-end rollover)</span></label>
              <select
                value={form.promotion_decision}
                onChange={(e) => updateField('promotion_decision', e.target.value)}
                disabled={saving}
              >
                <option value="">Not set (rollover defaults to Promoted)</option>
                <option value="promoted">Promoted</option>
                <option value="repeated">Repeated</option>
                <option value="graduated">Graduated</option>
              </select>
            </div>
            <div className="form-group">
              <label>Promoted To <span className="field-hint">(printed label, Term 3 only)</span></label>
              <input
                type="text"
                value={form.promoted_to}
                onChange={(e) => updateField('promoted_to', e.target.value)}
                placeholder="e.g., Primary 5"
                disabled={saving}
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-remarks-btn">
              {saving ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : 'Save Details'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ReportCardRemarksModal;
